import React from 'react';
import styles from './UsageGuide.module.css';

/**
 * One-pager for designers & developers explaining BCS Config in Coffer.
 * Linked from Help and from the info control next to the BCS button.
 */
function BcsFlowGuide() {
    return (
        <div className={styles.usageGuidePageContainer}>
            <h3>BCS Config — how it works</h3>
            <p>
                Use this guide when you need to turn an illustration on or off
                for specific apps (App IDs) via <strong>Business Config Service
                (BCS)</strong>, without waiting for an app store release.
            </p>

            <h5>What is BCS Config in Coffer?</h5>
            <p>
                BCS stores remote configuration that mobile apps read at runtime.
                For illustrations, Coffer can write a Darkroom image URL into BCS
                for selected App IDs. When an App ID is enabled, apps that match
                that ID can load the illustration from Darkroom. When disabled,
                that App ID no longer gets the illustration URL from BCS.
            </p>

            <h5>Who can use it?</h5>
            <ul>
                <li>
                    Users with permission to configure BCS (typically{' '}
                    <code>developer</code> / roles that include{' '}
                    <code>configure_bcs</code>).
                </li>
                <li>
                    Designers and developers who need to control which apps show
                    a given illustration remotely.
                </li>
            </ul>

            <h5>When does the BCS button appear?</h5>
            <ul>
                <li>On a <strong>default illustration</strong> detail page.</li>
                <li>
                    Only after the asset has a <strong>Darkroom URL</strong>{' '}
                    (generated or uploaded successfully).
                </li>
                <li>
                    If you do not see <code>BCS</code>, generate the Darkroom URL
                    first, or ask an admin to grant BCS permission.
                </li>
            </ul>

            <h5>Before you start</h5>
            <ol>
                <li>
                    Connect to the VPN shown in Coffer for your environment
                    (e.g. Integration VPN on staging, Production VPN on prod).
                </li>
                <li>Open the default illustration that already has a Darkroom URL.</li>
                <li>Click <code>BCS</code> next to Edit.</li>
            </ol>

            <h5>What you see in the BCS panel</h5>
            <ul>
                <li>
                    A list of <strong>App IDs</strong> (managed under Illustrations
                    → App IDs).
                </li>
                <li>
                    A toggle per App ID — <strong>on</strong> means BCS has an
                    illustration URL for that app; <strong>off</strong> means it
                    does not.
                </li>
                <li>
                    On <strong>staging / integration</strong> Coffer you may also
                    see an environment selector: <code>Integration</code> or{' '}
                    <code>QA</code>. Choose the BCS environment you intend to
                    update before loading or saving.
                </li>
                <li>
                    On <strong>production</strong> Coffer the selector includes{' '}
                    <code>Integration</code>, <code>QA</code>, and{' '}
                    <code>Production</code>.
                </li>
            </ul>

            <h5>How to enable or disable an illustration for apps</h5>
            <ol>
                <li>Open BCS Config for the asset.</li>
                <li>
                    (If shown) Select the BCS environment — Integration / QA on
                    staging, or Integration / QA / Production on prod.
                </li>
                <li>Toggle the App IDs you want on or off.</li>
                <li>
                    Click <code>Save Configuration</code>. Only changed toggles
                    are sent to BCS.
                </li>
            </ol>

            <h5>Automatic BCS refresh after Darkroom updates</h5>
            <p>
                When a default illustration’s Darkroom URL is created or updated
                in Coffer, Coffer also tries to refresh existing BCS entries that
                already point at an illustration URL for that asset — so apps keep
                using the latest image.
            </p>
            <ul>
                <li>
                    If that refresh fails, the Darkroom update can still succeed.
                    You will see a message asking you to open <code>BCS</code> and
                    save again.
                </li>
                <li>
                    On staging, auto-refresh may update both Integration and QA
                    BCS when applicable.
                </li>
            </ul>

            <h5>App IDs</h5>
            <p>
                App IDs are the application identifiers BCS uses as conditions
                (e.g. consumer app variants). Maintain the list under{' '}
                <strong>Illustrations → App IDs</strong>. BCS Config only shows
                App IDs for the environment you are configuring.
            </p>

            <h5>Quick troubleshooting</h5>
            <ul>
                <li>
                    <strong>“Connect to the … VPN…”</strong> — connect to the VPN
                    required for that Coffer environment, then reopen BCS.
                </li>
                <li>
                    <strong>Could not load BCS configuration</strong> — VPN, permission,
                    or the asset may not exist yet in BCS. Retry after VPN is up;
                    if it persists, contact the Coffer / platform team.
                </li>
                <li>
                    <strong>Empty App ID list</strong> — add App IDs for that
                    environment on the App IDs page.
                </li>
                <li>
                    <strong>Toggles look wrong after a Darkroom change</strong> —
                    open BCS Config and save once to resync.
                </li>
            </ul>

            <h5>For developers (short technical picture)</h5>
            <ol>
                <li>Browser (Coffer) calls the platform BFF under <code>/api/coffer/bcs/…</code>.</li>
                <li>BFF authenticates the user, then proxies to BCS with the service token.</li>
                <li>
                    GET loads current mobile config conditions; PUT updates App ID
                    scoped values with the Darkroom <code>illustration_url</code>.
                </li>
                <li>
                    Staging can target Integration or QA BCS via the env selector;
                    production targets production BCS only.
                </li>
            </ol>

            <p>
                <strong>Need help?</strong> Use <code>Help → Contact us</code> or
                reach out to the Coffer maintainers.
            </p>
        </div>
    );
}

export default BcsFlowGuide;
